import ReactTextareaAutosize from "react-textarea-autosize";
import { SubmitHandler, useForm } from "react-hook-form";
import { useCallback, useEffect, useRef, useState } from "react";
import type { BotMsg, OpenAiUsage, UserMsg } from "../types";
import { BotMessage } from "./BotMessage";
import {
  CHATBOT_BLOCK_FIELD_NAME_VALUE,
  CHATBOT_BLOCK_FIELD_PLACEHOLDER_VALUE,
} from "./constants";
import { Payload, useChatCompletion } from "./useChatCompletion";
import { useTrackingContext } from "observability";
import { Icon } from "@zapier/design-system";
import type { ChatbotBlock } from "blocks/v2/Chatbot/schema";
import { datadogRum } from "@datadog/browser-rum";
import { usePageContext } from "lib/context/page-context";
import { noop } from "lodash";
import { useCurrentProject } from "lib/context/current-project-context";
import PoweredByZapier from "components/PoweredByZapier";
import { cn } from "utils/cn";
import { userHasAccess } from "lib/utils/stripe";

export type UserPromptProps = Payload &
  Partial<
    Pick<
      ChatbotBlock["config"],
      "dataSources" | "dataSourcesNoResultsMessage" | "dataSourcesNoResultsMode"
    >
  > & {
    chatbotName?: string;
    isDisabled?: boolean;
    model: ChatbotBlock["config"]["model"];
    placeholder?: string;
    onChatComplete: (
      userMsg: UserMsg,
      botMsg: BotMsg,
      jumpToBottom?: boolean
    ) => void;
    onSubmitPrompt?: (userMsg: UserMsg) => void;
    onLiveBotResponseUpdated?: (botMsg: string) => void;
    onUsage: (usage: OpenAiUsage) => void;
    isInPopup?: boolean;
  };

const defaultValues: UserMsg = {
  role: "user",
  content: "",
};

const PoweredByZapierContainer: React.FunctionComponent<
  React.ComponentPropsWithoutRef<"div">
> = (props) => (
  <div
    {...props}
    className={cn(
      "border-t-solid border-t border-t-zi-pageBackground bg-zi-pageBackground py-1 [&_a]:font-sans [&_a]:text-xs [&_a]:font-semibold [&_svg]:h-3",
      props.className
    )}
  />
);

export function UserPrompt({
  blockId,
  chatbotName = CHATBOT_BLOCK_FIELD_NAME_VALUE,
  isDisabled = false,
  model,
  placeholder = CHATBOT_BLOCK_FIELD_PLACEHOLDER_VALUE,
  onChatComplete,
  onUsage,
  messages,
  onSubmitPrompt = noop,
  onLiveBotResponseUpdated = noop,
  isInPopup = false,
}: UserPromptProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const textarea = formRef.current?.querySelector("textarea");
  const { pageId, projectId, isEmbedded } = usePageContext();

  const [runChatCompletion, resultRef] = useChatCompletion(
    model,
    onUsage,
    onLiveBotResponseUpdated
  );
  const { emitConsumerInteractionEvent } = useTrackingContext();

  const {
    register,
    handleSubmit,
    reset,
    resetField,
    setError,
    setFocus,
    setValue,
    formState: { isSubmitting, isSubmitSuccessful, errors },
  } = useForm({
    defaultValues,
  });

  const [errorCode, setErrorCode] = useState<string>("");

  const handleEnter = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      formRef.current?.querySelector("button")?.click();
    }
  };

  const onSubmit = useCallback<SubmitHandler<UserMsg>>(
    async (userMsg) => {
      if (userMsg.content === "") return;
      resetField("content");

      setErrorCode("");

      emitConsumerInteractionEvent({
        interaction_goal: "send message in chatbot",
        interaction_raw_text: null,
        interfaces_component_type: "chatbot",
        event_action: "prompt",
        interfaces_project_id: projectId,
        interfaces_page_id: pageId,
        embedded_flag: isEmbedded,
      });

      onSubmitPrompt(userMsg);

      const { status, code } = await runChatCompletion({
        messages: messages.concat(userMsg),
        blockId,
      });

      if (status === "error") {
        setValue("content", userMsg.content);

        datadogRum.addError(`Chat response error: ${code}`);
        setErrorCode(code);
        setError(
          "content",
          {
            type: "custom",
            message: resultRef.current,
          },
          { shouldFocus: true }
        );
      } else {
        onChatComplete(userMsg, {
          role: "assistant",
          content: resultRef.current,
        });
        onLiveBotResponseUpdated("");
      }
    },
    [
      resetField,
      emitConsumerInteractionEvent,
      projectId,
      pageId,
      isEmbedded,
      onSubmitPrompt,
      runChatCompletion,
      messages,
      blockId,
      onChatComplete,
      onLiveBotResponseUpdated,
      setValue,
      setError,
      resultRef,
    ]
  );

  const focus = useCallback(
    function focusPrompt() {
      if (textarea) {
        textarea.focus();
        textarea.select();
      }
    },
    [textarea]
  );

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset();
      focus();
    }
  }, [isSubmitSuccessful, reset, setFocus, focus]);
  const project = useCurrentProject();

  return (
    <>
      {errors.content && (
        <>
          <BotMessage chatbotName={chatbotName} errorCode={errorCode} isError>
            {errors.content.message}
          </BotMessage>
        </>
      )}
      <form action="#" onSubmit={handleSubmit(onSubmit)} ref={formRef}>
        <fieldset
          className={cn(
            "ml-5 flex animate-fadeIn scroll-mb-[100px] items-start gap-2.5 border border-zi-lightGray bg-zi-white p-5 font-sans text-base text-zi-text transition-shadow focus-within:border-zi-primary focus-within:outline-none focus-within:ring-1 focus-within:ring-inset focus-within:ring-zi-primary",
            {
              "rounded-xl": !isInPopup,
              "border-t border-zi-lightPrimary": isInPopup,
              "rounded-bl-xl rounded-br-xl":
                isInPopup &&
                userHasAccess(project.creator.planId) &&
                project.hideZapierButton,
              "rounded-none":
                isInPopup &&
                (!userHasAccess(project.creator.planId) ||
                  !project.hideZapierButton),
            }
          )}
          disabled={isDisabled || isSubmitting}
        >
          <ReactTextareaAutosize
            className="grow resize-none border-0 bg-transparent p-0 text-base focus-within:outline-none"
            {...register("content", { shouldUnregister: false })}
            aria-label="chatbot-user-prompt"
            onKeyDown={handleEnter}
            placeholder={
              isSubmitting ? "Chatbot is responding..." : placeholder
            }
          />
          <button
            className="flex h-6 w-6 items-center justify-center rounded bg-zi-white text-zi-primary transition-shadow focus-within:outline-none focus-within:ring-2 focus-within:ring-zi-primary focus-within:ring-offset-2 focus-within:ring-offset-zi-white disabled:text-zi-lightGray"
            type="submit"
          >
            <Icon name="actionSendFill" size={20} />
            <span className="sr-only" data-testid="visually-hidden">
              Send
            </span>
          </button>
        </fieldset>
        {isInPopup && (
          <PoweredByZapierContainer>
            <PoweredByZapier
              alignment="center"
              position="relative"
              variant="light"
            />
          </PoweredByZapierContainer>
        )}
      </form>
    </>
  );
}
